import React, { useState, useEffect } from "react";
import { FaSave, FaTimes, FaFile, FaTrash } from "react-icons/fa";

interface SavedProject {
  _id: string;
  name: string;
  createdAt: number;
  updatedAt: number;
  data: any;
}

interface SaveLoadModalProps {
  mode: "save" | "load";
  currentProjectId?: string;
  onSave: (name: string) => void;
  onLoad: (project: SavedProject) => void;
  onClose: () => void;
  onShowToast: (message: string, type: "success" | "error" | "info") => void;
}

const LOCALSTORAGE_KEY = "nanimate-projects";

export default function SaveLoadModal({
  mode,
  currentProjectId,
  onSave,
  onLoad,
  onClose,
  onShowToast,
}: SaveLoadModalProps) {
  const [projects, setProjects] = useState<SavedProject[]>([]);
  const [fileName, setFileName] = useState("");
  const [selectedProject, setSelectedProject] = useState<string | null>(null);
  const [showImport, setShowImport] = useState(false);
  const [importJson, setImportJson] = useState("");

  useEffect(() => {
    loadProjects();
  }, []);

  const loadProjects = () => {
    const stored = localStorage.getItem(LOCALSTORAGE_KEY);
    const projectList = stored ? JSON.parse(stored) : [];
    setProjects(projectList);
  };

  const deleteProject = (projectId: string) => {
    const filtered = projects.filter((p) => p._id !== projectId);
    localStorage.setItem(LOCALSTORAGE_KEY, JSON.stringify(filtered));
    setProjects(filtered);
  };

  const handleSave = () => {
    if (!fileName.trim()) return;
    onSave(fileName.trim());
    onClose();
  };

  const handleLoad = () => {
    const project = projects.find((p) => p._id === selectedProject);
    if (project) {
      onLoad(project);
      onClose();
    }
  };

  const handleImport = () => {
    if (!importJson.trim()) {
      onShowToast("Please paste JSON data to import", "error");
      return;
    }

    try {
      const projectData = JSON.parse(importJson);

      // Validate the structure
      if (!projectData._id || !projectData.layers) {
        onShowToast("Invalid project format: missing required fields", "error");
        return;
      }

      // Create a SavedProject object
      const now = Date.now();
      const project: SavedProject = {
        _id: projectData._id,
        name:
          projectData.name || `Imported Project ${new Date().toLocaleString()}`,
        createdAt: now,
        updatedAt: now,
        data: projectData,
      };

      // Save to localStorage
      const stored = localStorage.getItem(LOCALSTORAGE_KEY);
      const projectList = stored ? JSON.parse(stored) : [];

      // Check if project with same ID exists
      const existingIndex = projectList.findIndex(
        (p: SavedProject) => p._id === project._id,
      );
      if (existingIndex > -1) {
        projectList[existingIndex] = project;
        onShowToast("Project updated successfully", "success");
      } else {
        projectList.push(project);
        onShowToast("Project imported successfully", "success");
      }

      localStorage.setItem(LOCALSTORAGE_KEY, JSON.stringify(projectList));

      // Load the imported project
      onLoad(project);
      onClose();
    } catch (error) {
      onShowToast(`Import failed: ${error.message}`, "error");
    }
  };

  const formatDate = (timestamp: number) => {
    const date = new Date(timestamp);
    const now = new Date();
    const isToday = date.toDateString() === now.toDateString();

    if (isToday) {
      return date.toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "2-digit",
      });
    }

    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: date.getFullYear() !== now.getFullYear() ? "numeric" : undefined,
    });
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal-content save-load-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header">
          <h2>{mode === "save" ? "Save Project" : "Load Project"}</h2>
          <button className="modal-close" onClick={onClose}>
            <FaTimes />
          </button>
        </div>

        <div className="modal-body">
          {mode === "save" ? (
            <div className="save-section">
              <label htmlFor="project-name">Project Name</label>
              <input
                id="project-name"
                type="text"
                placeholder="Enter project name..."
                value={fileName}
                onChange={(e) => setFileName(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSave()}
                autoFocus
              />
              <p className="help-text">
                {currentProjectId
                  ? "Save will update the existing project"
                  : "A new project will be created"}
              </p>
            </div>
          ) : showImport ? (
            <div className="import-section">
              <label htmlFor="import-json">Paste Project JSON</label>
              <textarea
                id="import-json"
                placeholder="Paste your project JSON here..."
                value={importJson}
                onChange={(e) => setImportJson(e.target.value)}
                autoFocus
                rows={15}
              />
              <p className="help-text">
                Paste the JSON export of a Nanimate project to import it
              </p>
            </div>
          ) : (
            <div className="load-section">
              {projects.length === 0 ? (
                <div className="empty-projects">
                  <FaFile />
                  <p>No saved projects yet</p>
                </div>
              ) : (
                <div className="project-list">
                  {projects.map((project) => (
                    <div
                      key={project._id}
                      className={`project-item ${selectedProject === project._id ? "selected" : ""}`}
                      onClick={() => setSelectedProject(project._id)}
                    >
                      <div className="project-icon">
                        <FaFile />
                      </div>
                      <div className="project-info">
                        <div className="project-name">{project.name}</div>
                        <div className="project-meta">
                          <span>Created {formatDate(project.createdAt)}</span>
                          {project.updatedAt !== project.createdAt && (
                            <>
                              <span className="separator">•</span>
                              <span>
                                Updated {formatDate(project.updatedAt)}
                              </span>
                            </>
                          )}
                        </div>
                      </div>
                      <button
                        className="delete-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          deleteProject(project._id);
                        }}
                        title="Delete project"
                      >
                        <FaTrash />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="modal-footer">
          {mode === "load" && !showImport && (
            <button
              className="btn btn-secondary"
              onClick={() => setShowImport(true)}
              style={{ marginRight: "auto" }}
            >
              Import
            </button>
          )}
          {mode === "load" && showImport && (
            <button
              className="btn btn-secondary"
              onClick={() => {
                setShowImport(false);
                setImportJson("");
              }}
              style={{ marginRight: "auto" }}
            >
              Back
            </button>
          )}
          <button className="btn btn-secondary" onClick={onClose}>
            Cancel
          </button>
          {mode === "save" ? (
            <button
              className="btn btn-primary"
              onClick={handleSave}
              disabled={!fileName.trim()}
            >
              <FaSave />
              Save
            </button>
          ) : showImport ? (
            <button
              className="btn btn-primary"
              onClick={handleImport}
              disabled={!importJson.trim()}
            >
              Load
            </button>
          ) : (
            <button
              className="btn btn-primary"
              onClick={handleLoad}
              disabled={!selectedProject}
            >
              Load
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
